import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { 
  HomeIcon, 
  InformationCircleIcon, 
  MagnifyingGlassIcon, 
  CalendarIcon, 
  ChatBubbleLeftIcon, 
  Bars3Icon, 
  XMarkIcon 
} from '@heroicons/react/24/outline';

export default function Navbar() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const navLinks = [
    { to: "/", label: "Home", icon: <HomeIcon className="h-5 w-5 mr-1.5" /> },
    { to: "/about", label: "About Us", icon: <InformationCircleIcon className="h-5 w-5 mr-1.5" /> },
    { to: "/browse", label: "Browse Cars", icon: <MagnifyingGlassIcon className="h-5 w-5 mr-1.5" /> },
    { to: "/book-inspection", label: "Book Inspection", icon: <CalendarIcon className="h-5 w-5 mr-1.5" /> },
    { to: "/connect", label: "Connect with Us", icon: <ChatBubbleLeftIcon className="h-5 w-5 mr-1.5" /> }
  ];

  return (
    <nav className="bg-white/95 backdrop-blur-md text-gray-800 py-3 px-4 sticky top-0 z-40 shadow-sm border-b border-gray-100 transition-all duration-300">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        
        {/* Logo and Brand Name */}
        <NavLink to="/" className="flex items-center space-x-3 group">
          <img 
            src="/img/sajies.jpg" 
            alt="Sajies Global Synergy Limited Logo" 
            className="h-10 w-10 object-contain rounded-md border border-gray-100 transition-transform duration-300 group-hover:scale-105" 
          />
          <div className="flex flex-col">
            <span className="text-sm sm:text-lg font-bold text-gray-900 tracking-tight leading-none group-hover:text-blue-900 transition-colors">
              SAJIES GLOBAL
            </span>
            <span className="text-[9px] sm:text-xs text-red-600 font-semibold tracking-wider uppercase">
              Synergy Limited
            </span>
          </div>
        </NavLink>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `text-sm font-medium flex items-center px-3 py-2 rounded-md transition-all duration-200 ${
                  isActive 
                    ? 'text-blue-900 bg-blue-50/50 font-semibold' 
                    : 'text-gray-600 hover:text-red-600 hover:bg-gray-50'
                }`
              }
            >
              {link.icon}
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* Mobile Toggle Button */}
        <div className="md:hidden">
          <button 
            onClick={toggleSidebar} 
            className="text-gray-700 hover:text-red-600 focus:outline-none p-2 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Toggle Menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Fullscreen Overlay - Tapping here triggers toggleSidebar to close the menu */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 w-full h-full bg-black/40 backdrop-blur-sm z-40 transition-opacity md:hidden cursor-pointer" 
          onClick={toggleSidebar}
        />
      )}
      
      {/* Mobile Sidebar */}
      <div
        className={`fixed inset-y-0 right-0 h-screen w-72 bg-white shadow-2xl transform ${
          isSidebarOpen ? 'translate-x-0 opacity-100 visible' : 'translate-x-full opacity-0 invisible'
        } transition-all duration-300 ease-in-out md:hidden z-50 flex flex-col`}
      >
        <div className="p-5 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
          <span className="text-sm font-bold text-blue-900 tracking-wider">NAVIGATION</span>
          <button
            onClick={toggleSidebar}
            className="text-gray-500 hover:text-red-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
            aria-label="Close Menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="flex flex-col space-y-3.5 p-6 overflow-y-auto">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={toggleSidebar}
              className={({ isActive }) =>
                `text-base font-semibold flex items-center p-3 rounded-lg transition-all duration-200 ${
                  isActive 
                    ? 'text-white bg-blue-900 shadow-md' 
                    : 'text-gray-700 hover:bg-gray-100 hover:text-red-600'
                }`
              }
            >
              <div className="mr-3">{link.icon}</div>
              {link.label}
            </NavLink>
          ))}
        </div>
        <div className="mt-auto p-6 border-t border-gray-100 bg-gray-50 text-center text-xs text-gray-500 flex-shrink-0">
          © 2025 Sajies Global Synergy
        </div>
      </div>
    </nav>
  );
}
